import React, { useState, useEffect } from "react";
import {
  Tabs,
  Tab,
  Box,
  Grid,
  Paper,
  Menu,
  Badge,
  ButtonBase,
  Avatar,
} from "@material-ui/core";
import AppsIcon from "@material-ui/icons/Apps";
import { useHistory, useLocation } from "react-router-dom";
import dashboardType from "../../../store/dashboard_type";

// Import your components
import LogoSection from "../LogoSection";
import ProfileSection from "./ProfileSection";

// Import your images
import TempImage from "./../../../assets/images/icons/Temp.png";
import UPSImage from "./../../../assets/images/icons/Ups.png";
import DGImage from "./../../../assets/images/icons/Dg.png";
import WaterImage from "./../../../assets/images/icons/water.png";
import VoltImage from "./../../../assets/images/icons/ems.png";
import FireImage from "./../../../assets/images/icons/fire system.png";
import HvacImage from "./../../../assets/images/icons/hvac.png";
import VibImage from "./../../../assets/images/icons/vib.png";
import ShieldImage from "./../../../assets/images/icons/NBSafe.png";
import TransImage from "./../../../assets/images/icons/trans.png";
import CropImage from "./../../../assets/images/icons/crop.png";
import GasImage from "./../../../assets/images/icons/gas.png";
import SteamImage from "./../../../assets/images/icons/steam.png";
import AirImage from "./../../../assets/images/icons/aircompress.png";
import TruckImage from "./../../../assets/images/icons/truck.png";
import Solar from "./../../../assets/images/icons/solar.png";

// assets
import { IconMenu2 } from "@tabler/icons";
import { makeStyles } from "@material-ui/styles";

// style constant
const useStyles = makeStyles((theme) => ({
  grow: {
    flexGrow: 0,
  },
  headerAvatar: {
    ...theme.typography.commonAvatar,
    ...theme.typography.mediumAvatar,
    transition: "all .2s ease-in-out",
    background: theme.palette.secondary.light,
    color: theme.palette.secondary.dark,
    "&:hover": {
      background: theme.palette.secondary.dark,
      color: theme.palette.secondary.light,
    },
  },
  boxContainer: {
    display: "flex",
    alignItems: "center",
    [theme.breakpoints.up("md")]: {
      marginRight: "1%",
    },
    [theme.breakpoints.up("sm")]: {
      marginRight: "1",
    },
  },
  subHeading: {
    width: "150px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    height: "40px",
    boxShadow:
      "rgba(0, 0, 0, 0.25) 0px 0.0625em 0.0625em, rgba(0, 0, 0, 0.25) 0px 0.125em 0.5em, rgba(255, 255, 255, 0.1) 0px 0px 0px 1px inset",
  },
  tagGroup: {
    display: "none",
    [theme.breakpoints.up("md")]: {
      display: "flex",
      justifyContent: "center",
    },
    [theme.breakpoints.up("sm")]: {
      display: "flex",
      justifyContent: "center",
    },
  },
}));

const Header = ({ handleLeftDrawerToggle, currentIndex = 0 }) => {
  const classes = useStyles();
  const [value, setValue] = React.useState(currentIndex);
  const history = useHistory();
  const location = useLocation();
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [activeTab, setActiveTab] = useState(false);

  let initialTabsData = [
    { label: "Energy", image: VoltImage, path: "/ems/home" },
    { label: "Water", image: WaterImage, path: "/wms/home" },
    { label: "DG", image: DGImage, path: "/dg/home" },
    { label: "Transformer", image: TransImage, path: "/trans/home" },
    { label: "UPS", image: UPSImage, path: "/bms/home" },
    { label: "Air Quality", image: TempImage, path: "/temp/home" },
    { label: "HVAC", image: HvacImage, path: "/hvc/home" },
    { label: "Vibration", image: VibImage, path: "/vib/home" },
    { label: "Fire System", image: FireImage, path: "/fms/home" },
    { label: "Steam Flow", image: SteamImage, path: "/steam/home" },
    { label: "NBSafe", image: ShieldImage, path: "/nbs/home" },
    { label: "Agriculture", image: CropImage, path: "/agr/home" },
  ];

  let moreTabsData = [
    { label: "Gas Flow", image: GasImage, path: "/gas/home" },
    { label: "Air Compressor", image: AirImage, path: "/air/home" },
    { label: "FLMS", image: TruckImage, path: "/flms/home" },
    { label: "Solar", image: Solar, path: "/solar/home" },
  ];

  // Handle admin / superAdmin
  if (
    currentIndex === dashboardType.admin ||
    currentIndex === dashboardType.superAdmin ||
    currentIndex === dashboardType.supportAdmin
  ) {
    initialTabsData = [];
    moreTabsData = [];
  }

  // Promote active tab from 'More' if needed
  // Promote active tab from 'More' to the BEGINNING if needed
  const matchingMoreIndex = moreTabsData.findIndex((tab) =>
    location.pathname.startsWith(tab.path)
  );

  if (matchingMoreIndex !== -1) {
    const activeMoreTab = moreTabsData[matchingMoreIndex];
    // Add to the beginning of initialTabsData
    initialTabsData = [activeMoreTab, ...initialTabsData];
    // Remove from moreTabsData
    moreTabsData = moreTabsData.filter((_, i) => i !== matchingMoreIndex);
  }

  // Update active tab
  useEffect(() => {
    const currentIndex = initialTabsData.findIndex((tab) =>
      location.pathname.startsWith(tab.path)
    );
    if (currentIndex !== -1) {
      setActiveTab(currentIndex);
    } else {
      setActiveTab(false);
    }
  }, [location.pathname, initialTabsData]);

  const handleOpenMenu = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const handleRedirect = (path) => {
    history.push(path);
    handleCloseMenu();
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        borderBottom: "1px solid #e8e8e8",
        backgroundColor: "#fff",
        px: 2,
      }}
    >
      <div className={classes.boxContainer}>
        {/* Left: Logo */}
        <Box
          component="span"
          sx={{ display: { xs: "none", md: "block" }, flexGrow: 1 }}
        >
          <LogoSection />
        </Box>

        <ButtonBase
          sx={{
            display: { xs: "block", md: "none" },
            borderRadius: "3px",
            overflow: "hidden",
            padding: 0,
          }}
        >
          <Avatar
            variant="rounded"
            className={classes.headerAvatar}
            onClick={handleLeftDrawerToggle}
            color="inherit"
          >
            <IconMenu2 stroke={3.5} size="1rem" />
          </Avatar>
        </ButtonBase>
      </div>
      {/* Center: Tabs */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Tabs
          value={activeTab === false ? false : activeTab}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: "48px",
            "& .MuiTab-root": {
              minHeight: "48px",
              padding: "4px 12px",
              textTransform: "none",
            },
          }}
        >
          {initialTabsData.map((tab, index) => (
            <Tab
              key={index}
              onClick={() => handleRedirect(tab.path)}
              icon={
                <Box display="flex" flexDirection="column" alignItems="center">
                  <img
                    src={tab.image}
                    alt={tab.label}
                    style={{
                      width: "20px",
                      height: "20px",
                      marginBottom: "4px",
                    }}
                  />
                  <span
                    style={{
                      fontWeight: 700,
                      fontSize: "clamp(10px, 1.2vw, 12px)",
                      textAlign: "center",
                    }}
                  >
                    {tab.label}
                  </span>
                </Box>
              }
              sx={{
                minWidth: "auto",
                padding: "6px",
                flexShrink: 0,
              }}
            />
          ))}

          {/* More Tab */}
          {moreTabsData.length > 0 && (
            <Tab
              onClick={handleOpenMenu}
              icon={
                // <Box display="flex" flexDirection="column" alignItems="center">
                //   <AppsIcon
                //     style={{
                //       fontSize: "20px",
                //       marginBottom: "4px",
                //       color: "#1976d2",
                //     }}
                //   />
                //   <span
                //     style={{
                //       fontWeight: 700,
                //       fontSize: "clamp(10px, 1.2vw, 12px)",
                //       textAlign: "center",
                //       color: "#1976d2",
                //     }}
                //   >
                //     More
                //   </span>
                // </Box>
                <Box display="flex" flexDirection="column" alignItems="center">
                  <Box
                    sx={{
                      width: 25,
                      height: 25,
                      border: "2px solid #1976d2",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#1976d2",
                      fontWeight: 700,
                      fontSize: "clamp(10px, 1.4vw, 13px)",
                      mb: 0.2,
                    }}
                  >
                    +{moreTabsData.length}
                  </Box>
                  <span
                    style={{
                      fontWeight: 700,
                      fontSize: "clamp(10px, 1.2vw, 12px)",
                      textAlign: "center",
                      color: "#1976d2",
                    }}
                  >
                    More
                  </span>
                </Box>
              }
              sx={{
                minWidth: "auto",
                padding: "6px",
                flexShrink: 0,
              }}
            />
          )}
        </Tabs>
      </Box>

      {/* Right: Profile */}
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <ProfileSection />
      </Box>

      {/* Menu for More Tabs */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenu}
        MenuListProps={{
          sx: { padding: 1 },
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Grid container spacing={1} sx={{ width: 300, p: 1 }}>
          {moreTabsData.map((tab, index) => (
            <Grid item xs={4} key={index}>
              <Paper
                elevation={3}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 1,
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "#f5f5f5" },
                }}
                onClick={() => handleRedirect(tab.path)}
              >
                <img
                  src={tab.image}
                  alt={tab.label}
                  style={{
                    width: "20px",
                    height: "20px",
                    marginBottom: "4px",
                  }}
                />
                <span
                  style={{
                    fontSize: "clamp(10px, 1.2vw, 12px)",
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    padding: "2px 0px 2px 0px",
                  }}
                >
                  {tab.label}
                </span>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Menu>
    </Box>
  );
};

export default Header;
